import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  toGetHyperPayKeys,
  toPrepareCheckout,
} from "../../apis/actions/paymentMethods.actions";
import { AppDispatch, RootState } from "../../apis/store";
import { getGrandPayableTotalFromState } from "../../utils/paymentTotals";
import { getHyperPayBaseUrl } from "../../utils/hyperPay";

type HyperPayEntityField = "entityId" | "entityIdMada";

/** One HyperPay `POST /checkouts` per modal open (avoids cart/amount dep churn). */
export function useHyperPayCheckoutPrepare(
  isOpen: boolean,
  entityField: HyperPayEntityField,
  onCheckoutId: (id: string | null, integrity?: string) => void,
  onLoading: (loading: boolean) => void,
  /** Charge this instead of the cart total (wallet top-up enters its own amount). */
  amountOverride?: number
) {
  const dispatch = useDispatch<AppDispatch>();
  const preparedForSessionRef = useRef(false);

  const { donationAmount, amount, walletAmount, services, products, discountAmount, cart_id } =
    useSelector((state: RootState) => state.global);
  const profile = useSelector((state: RootState) => state.login?.profil?.data);
  const { keys } = useSelector((state: RootState) => state.tabby.HyperPayKeys);
  const hyperPayBaseUrl = getHyperPayBaseUrl(keys);
  const entityId = keys?.[entityField] as string | undefined;

  useEffect(() => {
    if (!isOpen) return;
    dispatch(toGetHyperPayKeys());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      preparedForSessionRef.current = false;
      return;
    }

    if (!entityId || !keys?.token || !hyperPayBaseUrl) {
      return;
    }

    if (preparedForSessionRef.current) {
      return;
    }
    preparedForSessionRef.current = true;

    const totalAmount =
      amountOverride != null && amountOverride > 0
        ? amountOverride
        : getGrandPayableTotalFromState(
            amount,
            donationAmount,
            walletAmount,
            services,
            products,
            discountAmount
          );

    const [givenName, ...rest] = String(profile?.name ?? "Customer").trim().split(/\s+/);
    // ponytail: profile has no address yet; defaults satisfy 3DS2. Send real address once Odoo exposes it.
    const data: Record<string, string> = {
      entityId,
      amount: totalAmount.toFixed(2),
      currency: "SAR",
      paymentType: "DB",
      integrity: "true",
      merchantTransactionId: cart_id ? String(cart_id) : `txn_${Date.now()}`,
      "customer.email": profile?.email || "customer@example.com",
      "customer.givenName": givenName,
      "customer.surname": rest.join(" ") || givenName,
      // MIGS rejects "/" in the street ("Invalid request parameters!").
      "billing.street1": String(profile?.street || "Riyadh").replace(/\//g, " "),
      "billing.city": profile?.city || "Riyadh",
      "billing.state": profile?.state || "Riyadh",
      "billing.country": "SA",
      "billing.postcode": profile?.zip || "00000",
    };
    if (hyperPayBaseUrl.includes("eu-test.")) {
      // ponytail: INTERNAL = HyperPay simulator; EXTERNAL routes to MIGS test host which is down. Back to EXTERNAL once HyperPay fixes it.
      data.testMode = "INTERNAL";
      data["customParameters[3DS2_enrolled]"] = "true";
      data["customParameters[3DS2_flow]"] = "challenge";
    }

    let cancelled = false;
    onCheckoutId(null);
    onLoading(true);

    (async () => {
      try {
        const result = await dispatch(
          toPrepareCheckout({
            data,
            token: keys.token,
            hyperPayUrl: hyperPayBaseUrl,
          })
        );
        if (cancelled) return;
        if (toPrepareCheckout.fulfilled.match(result)) {
          const id = result.payload?.data?.id ?? null;
          onCheckoutId(id, result.payload?.data?.integrity);
          if (!id) {
            onLoading(false);
          }
          return;
        }
        onLoading(false);
      } catch {
        if (!cancelled) {
          onLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      preparedForSessionRef.current = false;
    };
    // Total is fixed when the modal opens; omit cart slices to avoid duplicate /checkouts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId, keys?.token, hyperPayBaseUrl, amountOverride]);

  return { keys, hyperPayBaseUrl };
}
